import { Link, useLocation } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import { Band } from '../layout/health-band';
import { bandFor, type PlanState } from './band';

/**
 * Полоса о сроке подписки над кабинетом (FR-5.3).
 *
 * ═══════════════════════════════════════════════════════════════════════
 * ЗАМОРОЗКА НЕ ДОЛЖНА БЫТЬ ОТКРЫТИЕМ.
 *
 * До сегодняшнего дня об окончании подписки говорили два места:
 * письмо за неделю (его читают не все) и подпись в закрытом ящике.
 * В самом кабинете — ничего. Человек узнавал о заморозке по тому,
 * что ставки перестали меняться, и это первое, что он подумает:
 * «сервис сломался».
 *
 * ПОЛОСА ВСЕГДА ВЕДЁТ НА ЭКРАН ТАРИФА. Сообщение без ответа на «и что
 * мне теперь делать» — это половина сообщения.
 * ═══════════════════════════════════════════════════════════════════════
 */
export function PlanBand({ wsId }: { wsId: string }) {
  const location = useLocation();
  const q = useQuery({
    queryKey: ['billing', wsId],
    queryFn: () => api<{ current: PlanState | null }>(`/workspaces/${wsId}/billing`),
    enabled: Boolean(wsId),
    retry: false,
    // Срок меняется раз в сутки: спрашивать чаще нечего
    staleTime: 5 * 60_000,
  });

  // ОТКАЗ МОЛЧИТ. Полоса «не удалось узнать про подписку» не говорит
  // человеку ничего, что он мог бы сделать, — а место занимает то же,
  // что настоящая тревога
  if (q.error || q.data === undefined) return null;

  const band = bandFor(q.data.current);
  if (band === null) return null;

  /**
   * НА САМОМ ЭКРАНЕ ТАРИФА ПОЛОСЫ НЕТ.
   *
   * Там то же самое написано в карточке подписки — целиком, а не
   * первой фразой, — и ссылка вела бы на страницу, на которой человек
   * уже стоит. Полоса нужна там, откуда про подписку не видно.
   */
  if (location.pathname.endsWith('/plan')) return null;

  return (
    <Band level={band.level} повод={band.text}>
      {band.text}{' '}
      <Link className="rk-link" to="plan" style={{ color: 'inherit' }}>
        тариф и оплата
      </Link>
    </Band>
  );
}
